import { PhotoStudioGenerationStepId } from '../generationSteps'
import { useCallback } from 'react'
import { logger } from 'utils/logging'
import { useDeclutterProcessor } from './useDeclutterProcessor.hook'

interface UseDeclutterFlowProps {
  assignmentId: string
  sourceVisualFilename: string
  getLatestPreviewFilename: () => string | null
  onProgressUpdate?: (step: string) => void
  onComplete?: (resultUrl: string) => void
  onError?: (error: any) => void
}

/**
 * Complete Declutter flow hook
 * Handles processing and result retrieval
 *
 * Usage:
 * 1. Call processDeclutterRoom
 * 2. Hook handles processing, polling, and result retrieval
 * 3. Calls onComplete with result URL when finished
 */
export const useDeclutterFlow = ({
  assignmentId,
  sourceVisualFilename,
  getLatestPreviewFilename,
  onProgressUpdate,
  onComplete,
  onError
}: UseDeclutterFlowProps) => {
  // Declutter processor
  const { processDeclutter, isProcessing, error } = useDeclutterProcessor({
    assignmentId,
    sourceVisualFilename,
    getLatestPreviewFilename,
    onProgressUpdate,
    onComplete,
    onError
  })

  const processDeclutterRoom = useCallback(async (
    previousJobId?: string
  ): Promise<string> => {
    try {
      onProgressUpdate?.(PhotoStudioGenerationStepId.START_PROCESSING)

      // Process Declutter directly (no mask/settings needed)
      const result = await processDeclutter(previousJobId)

      if (result.success) {
        return result.jobId!
      } else {
        throw result.error
      }
    } catch (error) {
      logger.error('Declutter: Flow failed', error, {
        context: {
          component: 'PhotoStudio',
          action: 'declutterFlow',
          metadata: { assignmentId }
        }
      })
      onError?.(error)
      throw error
    }
  }, [processDeclutter, onProgressUpdate, onError, assignmentId])

  return {
    processDeclutterRoom,
    isProcessing,
    error
  }
}
